import { useCallback, useEffect, useState } from "react";
import { Accordion } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { CreateLoaderTable } from "@/components/Loaders";
import { DISTRIBUTION_PROCESS_STATUS } from "@/constants/distributionStatus";
import { Inbox, RefreshCw } from "lucide-react";
import { BulkActionBar } from "./components/BulkActionBar";
import { BulkResultDialog, ConfirmDialog } from "./components/ConfirmDialog";
import { ContestGroup } from "./components/ContestGroup";
import { FilterBar } from "./components/FilterBar";
import { SummaryCards } from "./components/SummaryCards";
import { useBulkActions } from "./hooks/useBulkActions";
import { useDistributionData } from "./hooks/useDistributionData";
import { useDistributionFilters } from "./hooks/useDistributionFilters";
import { useDistributionSelection } from "./hooks/useDistributionSelection";
import { SURFACE_CARD } from "./styles";
import { ContestGroupType } from "./types";

const pendingOf = (contest: ContestGroupType) =>
  contest.items.filter(
    (item) => item.statusId === DISTRIBUTION_PROCESS_STATUS.PENDING
  );

export const SellersReceived = () => {
  const [openContests, setOpenContests] = useState<string[]>([]);

  const { state, items, totals, refetch, filesMap, loadingFiles, loadFilesForContest } =
    useDistributionData();

  const filters = useDistributionFilters(items);
  const selection = useDistributionSelection(filters.contests);

  const { clear: clearSelection } = selection;
  const { statusFilter, debouncedSearch, lotteryFilter, contests } = filters;

  const handleBulkFinished = useCallback(async () => {
    clearSelection();
    await refetch();
  }, [clearSelection, refetch]);

  const bulk = useBulkActions({ onFinished: handleBulkFinished });

  // Trocar de aba, buscar ou filtrar por loteria descarta a seleção.
  useEffect(() => {
    clearSelection();
  }, [statusFilter, debouncedSearch, lotteryFilter, clearSelection]);

  const handleAccordionChange = (values: string[]) => {
    setOpenContests(values);

    values.forEach((contestId) => {
      const contest = contests.find((entry) => entry.id === contestId);

      if (contest) loadFilesForContest(contest);
    });
  };

  return (
    <div className="flex flex-col gap-4 pb-28 sm:pb-24">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-2xl font-semibold">Distribuições Recebidas</h2>
          <p className="text-sm text-muted-foreground">
            Confira, aceite ou rejeite as cotas que foram distribuídas para você.
          </p>
        </div>
        <Button
          variant="outline"
          onClick={refetch}
          disabled={state === "loading" || bulk.isProcessing}
        >
          <RefreshCw
            className={`mr-2 h-4 w-4 ${state === "loading" ? "animate-spin" : ""}`}
          />
          Atualizar
        </Button>
      </div>

      <SummaryCards counts={totals.counts} pendingQuotas={totals.pendingQuotas} />

      <FilterBar
        search={filters.search}
        onSearchChange={filters.setSearch}
        lotteryFilter={filters.lotteryFilter}
        onLotteryChange={filters.setLotteryFilter}
        lotteryOptions={filters.lotteryOptions}
        statusFilter={filters.statusFilter}
        onStatusChange={filters.setStatusFilter}
        counts={filters.counts}
        hasActiveFilters={filters.hasActiveFilters}
        onClearFilters={filters.clearFilters}
      />

      {state === "loading" ? (
        <CreateLoaderTable cols={4} lines={6} />
      ) : state === "error" ? (
        <div className="rounded-lg border border-rose-300 bg-rose-50 p-6 text-center text-rose-800 dark:border-rose-900/60 dark:bg-rose-950/30 dark:text-rose-200">
          Não foi possível carregar suas distribuições. Tente atualizar a página.
        </div>
      ) : contests.length === 0 ? (
        <div
          className={`flex flex-col items-center gap-2 rounded-lg border border-dashed p-10 text-center ${SURFACE_CARD}`}
        >
          <Inbox className="h-8 w-8 text-muted-foreground" />
          <p className="font-medium text-foreground">
            Nenhuma distribuição encontrada
          </p>
          <p className="text-sm text-muted-foreground">
            {filters.hasActiveFilters
              ? "Ajuste os filtros para ver outros resultados."
              : "Você ainda não recebeu distribuições."}
          </p>
        </div>
      ) : (
        <Accordion
          type="multiple"
          value={openContests}
          onValueChange={handleAccordionChange}
          className="flex flex-col gap-3"
        >
          {contests.map((contest) => (
            <ContestGroup
              key={contest.id}
              contest={contest}
              selectionState={selection.contestState(contest.id)}
              isProcessing={bulk.isProcessing}
              filesMap={filesMap}
              isLoadingFiles={Boolean(loadingFiles[contest.id])}
              isSelected={selection.isSelected}
              onToggleItem={selection.toggle}
              onToggleContest={selection.toggleContest}
              onAcceptContest={(target) =>
                bulk.setPendingAction({
                  action: "accept",
                  items: pendingOf(target),
                  scope: "contest",
                  contestName: target.name,
                })
              }
              onAcceptItem={(item) =>
                bulk.setPendingAction({
                  action: "accept",
                  items: [item],
                  scope: "single",
                })
              }
              onRejectItem={(item) =>
                bulk.setPendingAction({
                  action: "reject",
                  items: [item],
                  scope: "single",
                })
              }
            />
          ))}
        </Accordion>
      )}

      <BulkActionBar
        selectedCount={selection.selectedCount}
        selectedQuotas={selection.selectedQuotas}
        isProcessing={bulk.isProcessing}
        onClear={clearSelection}
        onAccept={() =>
          bulk.setPendingAction({
            action: "accept",
            items: selection.selectedItems,
            scope: "selection",
          })
        }
        onReject={() =>
          bulk.setPendingAction({
            action: "reject",
            items: selection.selectedItems,
            scope: "selection",
          })
        }
      />

      <ConfirmDialog
        pendingAction={bulk.pendingAction}
        isProcessing={bulk.isProcessing}
        onCancel={bulk.cancelAction}
        onConfirm={bulk.confirmAction}
      />

      <BulkResultDialog
        failures={bulk.failures}
        processedCount={bulk.processedCount}
        onClose={bulk.closeFailures}
      />
    </div>
  );
};
